import { z } from "zod";
import type { RuleSet, Tier } from "./types";
import { formatDuration } from "../lib/duration";
import { defaultProtectedHours, normalizeProtectedHours, parseClock, WEEKDAY_NAMES, WEEKDAYS } from "./protected-hours";
import { DEFAULT_END_LOCK_HOURS_BEFORE, DEFAULT_LOOKAHEAD_DAYS, DEFAULT_START_LOCK_HOURS_BEFORE, LOOKAHEAD_MAX_DAYS, LOOKAHEAD_MIN_DAYS, MAX_LOCK_HOURS_BEFORE } from "./constants";

export const DEFAULT_RULE_SET: RuleSet = {
  tiers: [
    { minGap: 90, maxGap: 119, blockMinutes: 30 },
    { minGap: 120, maxGap: 149, blockMinutes: 60 },
    { minGap: 150, maxGap: null, blockMinutes: 90 },
  ],
  minShiftMinutes: 240,
  removalUtilizationPct: 50,
  removalMinGapMinutes: 60,
  turnoverMaxMinutes: 30,
  lookaheadDays: DEFAULT_LOOKAHEAD_DAYS,
  startLockHoursBefore: DEFAULT_START_LOCK_HOURS_BEFORE,
  endLockHoursBefore: DEFAULT_END_LOCK_HOURS_BEFORE,
  protectedHours: defaultProtectedHours(),
  aiBookingEnabled: false,
  adoptManualBtb: false,
  keepSomeoneIn: false,
};

const tierSchema = z.object({
  minGap: z.number().int().min(0),
  maxGap: z.number().int().min(0).nullable(),
  blockMinutes: z.number().int().min(5),
});

const ruleSetSchema = z.object({
  tiers: z.array(tierSchema),
  minShiftMinutes: z.number().int().min(0),
  removalUtilizationPct: z.number().min(0).max(100),
  removalMinGapMinutes: z.number().int().min(0),
  // Defaulted, not required: a dashboard tab opened before this field existed still saves.
  turnoverMaxMinutes: z.number().int().min(0).max(60).default(30),
  // Defaulted for the same reason as turnoverMaxMinutes; the PUT route fills in the scope's current value first, so an old tab cannot reset it.
  lookaheadDays: z.number().int().min(LOOKAHEAD_MIN_DAYS).max(LOOKAHEAD_MAX_DAYS).default(DEFAULT_LOOKAHEAD_DAYS),
  // Defaulted like lookaheadDays, and the PUT route keeps the scope's current locks for an old tab.
  startLockHoursBefore: z.number().int().min(1).max(MAX_LOCK_HOURS_BEFORE).nullable().default(DEFAULT_START_LOCK_HOURS_BEFORE),
  endLockHoursBefore: z.number().int().min(1).max(MAX_LOCK_HOURS_BEFORE).nullable().default(DEFAULT_END_LOCK_HOURS_BEFORE),
  // Lenient on purpose: an old tab omits it and a partial object fills the rest with the default.
  protectedHours: z.unknown().optional().transform((v) => normalizeProtectedHours(v ?? {})),
  // Defaulted like lookaheadDays; the PUT route keeps the scope's current value for an old tab.
  aiBookingEnabled: z.boolean().default(false),
  adoptManualBtb: z.boolean().default(false),
  keepSomeoneIn: z.boolean().default(false),
});

export type ValidationResult = { ok: true; value: RuleSet } | { ok: false; errors: string[] };

const FIELD_MESSAGES: Record<string, string> = {
  tiers: "Each row needs whole-minute open times and a BTB block of at least 5 min",
  minShiftMinutes: "The minimum shift length must be a whole number of minutes, 0 or more",
  removalUtilizationPct: "The booked percentage must be between 0 and 100",
  removalMinGapMinutes: "The open time for removing a BTB block must be a whole number of minutes, 0 or more",
  turnoverMaxMinutes: "Cleaning / setup blocks must be 0 to 60 min",
  lookaheadDays: `Plan BTB blocks between ${LOOKAHEAD_MIN_DAYS} and ${LOOKAHEAD_MAX_DAYS} days ahead`,
  startLockHoursBefore: `The start time lock must be 1 to ${MAX_LOCK_HOURS_BEFORE} whole hours before the shift starts`,
  endLockHoursBefore: `The end time lock must be 1 to ${MAX_LOCK_HOURS_BEFORE} whole hours before the shift ends`,
  aiBookingEnabled: "AI booking must be on or off",
  adoptManualBtb: "Managers' BTB blocks must be on or off",
  keepSomeoneIn: "Keep someone in the salon must be on or off",
};

const PROTECTED_MESSAGES = {
  oneSided: (day: string) => `Protected hours on ${day} need both a start and an end`,
  reversed: (day: string) => `Protected hours on ${day} end before they start`,
};

const range = (min: number, max: number | null) => (max === null ? `${min} min and up` : `${min}–${max} min`);

export function validateRuleSet(input: unknown): ValidationResult {
  const parsed = ruleSetSchema.safeParse(input);
  if (!parsed.success) {
    // Owner-facing: one sentence per field, never the schema's field names.
    const msgs = parsed.error.issues.map((i) => FIELD_MESSAGES[String(i.path[0])] ?? "These rules could not be read");
    return { ok: false, errors: [...new Set(msgs)] };
  }
  const errors: string[] = [];
  const tiers = [...parsed.data.tiers].sort((a, b) => a.minGap - b.minGap);
  if (tiers.length === 0) errors.push("Add at least one row");
  for (let i = 0; i < tiers.length; i++) {
    const t = tiers[i];
    if (t.maxGap !== null && t.maxGap < t.minGap) errors.push(`The row starting at ${t.minGap} min ends before it starts (${t.maxGap} min)`);
    if (t.blockMinutes > t.minGap) errors.push(`The ${formatDuration(t.blockMinutes, "length")} BTB block is longer than the ${formatDuration(t.minGap)} of open time its rule starts at`);
    if (i < tiers.length - 1) {
      const next = tiers[i + 1];
      if (t.maxGap === null) errors.push(`Only the last row can be open-ended (${range(t.minGap, null)})`);
      else if (next.minGap <= t.maxGap) errors.push(`The ${range(t.minGap, t.maxGap)} and ${range(next.minGap, next.maxGap)} ranges overlap`);
      else if (next.minGap !== t.maxGap + 1) errors.push(`Nothing covers ${t.maxGap + 1}–${next.minGap - 1} min open: each range must start right after the previous one ends`);
    } else if (t.maxGap !== null) {
      errors.push(`The last row must be open-ended (${t.minGap} min and up)`);
    }
  }
  for (const d of WEEKDAYS) {
    const { protectFrom, protectTo } = parsed.data.protectedHours[d];
    if (protectFrom === null && protectTo === null) continue;
    if (protectFrom === null || protectTo === null) errors.push(PROTECTED_MESSAGES.oneSided(WEEKDAY_NAMES[d]));
    else if ((parseClock(protectFrom) ?? 0) >= (parseClock(protectTo) ?? 0)) errors.push(PROTECTED_MESSAGES.reversed(WEEKDAY_NAMES[d]));
  }
  if (errors.length) return { ok: false, errors };
  return { ok: true, value: { ...parsed.data, tiers } };
}

export function matchTier(gap: number, tiers: Tier[]): number | null {
  for (let i = 0; i < tiers.length; i++) {
    const t = tiers[i];
    if (gap >= t.minGap && (t.maxGap === null || gap <= t.maxGap)) return i;
  }
  return null;
}
